// ═══ PIE Section (Wheel Income) — extracted from PortfolioPage.js in iter133.
// Contains: BUCKET_COLORS constant, ModeBPanel, and the exported PIESection.
// All behavior identical to prior in-page definition — extraction only, no
// logic changes. See /app/memory/WHEEL_STRATEGIES_SPEC.md for the redesign.
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { verdictDisplay } from "@/lib/displayLabels";
import { formatProbPct, isValidProb, pairKey, PREP_PAIR_LIMIT, isRefreshEligible } from "@/lib/formatMarket";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  RefreshCw, Shield, Zap, Target, CheckCircle, AlertTriangle,
  RotateCw, Play, ChevronDown, ChevronUp, Gauge,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { ModeBRow } from "@/components/wheel/ModeBRow";
import { SuggestBasketModal } from "@/components/SuggestBasketModal";
import { RiskHealthPanel, useRiskHealth } from "@/components/RiskHealthLED";
import { DTETimeline, ExpiryChips, weeklyFridayExpirations } from "@/components/DTETimeline";

// Iter183a — collapsible Portfolio Risk Health strip for the Wheel page.
// Same data source (useRiskHealth → /portfolio/risk-health) as the nav LED,
// so the two surfaces can never disagree.
function WheelRiskHealthStrip() {
  const { health } = useRiskHealth();
  const [open, setOpen] = React.useState(false);
  if (!health) return null;
  const color = health.status === "red" ? "#EF4444" : health.status === "amber" ? "#F59E0B" : "#10B981";
  const breaches = (health.rules || []).filter(r => r.status === "amber" || r.status === "red");
  return (
    <div className="border rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
      data-testid="wheel-risk-health-strip">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-left"
        data-testid="wheel-risk-health-toggle">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
        <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>
          Portfolio Risk Health
        </span>
        <span className="text-[9px]" style={{ color: breaches.length ? color : "var(--hoip-text-tertiary)" }}>
          {breaches.length ? `${breaches.length} limit${breaches.length !== 1 ? "s" : ""} flagged — ${breaches.map(b => b.label).join(", ")}` : "all limits OK"}
        </span>
        <span className="ml-auto text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-3 pb-2">
          <RiskHealthPanel health={health} compact />
        </div>
      )}
    </div>
  );
}
import { SizingCoachRibbon } from "@/components/SizingCoachRibbon";
import { STRAT_COLORS, STRAT_ALL } from "@/components/wheel/wheelConstants";

// Iter D (iter135): hoisted to module scope so both ModeBPanel and the outer
// PIESection (below-floor drawer) can reference the same palette.
// Iter E (iter136): actual definitions moved to /components/wheel/wheelConstants.
// These re-exports preserve the internal API for any lingering consumers.
export { STRAT_COLORS as STRAT_COLORS_LOCAL, STRAT_ALL as STRAT_ALL_LOCAL };

const BUCKET_COLORS = { "Value / Core": "#3B82F6", "Growth / Alpha": "#10B981", "Turnaround / Momentum": "#F59E0B" };

function WheelDtePicker({ onSelectFriday, onUsePolicy, snappedExpiry, expiryPolicy, expiryMode, explicitExpiry }) {
  const expirations = weeklyFridayExpirations(90);
  const policyExpiry = expiryPolicy?.selected_expiry || snappedExpiry || null;
  const selectedDate = expiryMode === "explicit"
    ? (explicitExpiry || policyExpiry)
    : policyExpiry;
  const onSelect = (date) => {
    const hit = expirations.find((e) => e.date === date);
    if (hit) onSelectFriday(hit.date, hit.dte);
  };
  const banner = expiryPolicy?.banner;
  const win = expiryPolicy?.effective_window || expiryPolicy?.window;
  const modeLabel = expiryMode === "explicit" ? "Selected Friday" : "Policy default";
  return (
    <div className="border p-3 rounded-sm space-y-2 min-w-0"
      style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
      data-testid="income-period-select">
      <h3 className="text-[10px] font-bold mb-1" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-secondary)" }}>
        DTE Timeline
      </h3>
      <div className="text-[10px] leading-snug space-y-0.5" style={{ color: "var(--hoip-text-secondary)" }}
        data-testid="wheel-expiry-mode">
        <div><span className="font-bold">Mode:</span> {modeLabel}</div>
        {expiryPolicy?.label ? (
          <div data-testid="wheel-expiry-policy">
            <span className="font-bold">{expiryPolicy.label}</span>
            {win?.length === 2 ? <span>{` · ${win[0]}–${win[1]} DTE`}</span> : null}
            {expiryPolicy.prefer === "monthly" ? <span> · monthly preferred</span> : null}
          </div>
        ) : null}
        <div className="font-mono-data" data-testid="wheel-eval-expiry">
          Evaluated {policyExpiry || "—"}
          {expiryPolicy?.actual_dte != null ? ` · ${expiryPolicy.actual_dte} DTE` : ""}
        </div>
      </div>
      <DTETimeline
        availableExpirations={expirations}
        selectedExpiry={selectedDate}
        onSelectExpiry={onSelect}
        labelLimit={0}
      />
      <ExpiryChips
        expirations={expirations}
        selectedExpiry={selectedDate}
        setSelectedExpiry={onSelect}
        chipLimit={0}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" data-testid="wheel-use-policy-default"
          onClick={onUsePolicy}
          className="text-[10px] px-2 py-0.5 border rounded-sm"
          style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>
          Use policy default
        </button>
        {expiryPolicy?.indicative_dte != null ? (
          <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            {`target window chip ${expiryPolicy.indicative_dte}d`}
          </span>
        ) : null}
      </div>
      {snappedExpiry ? (
        <div className="text-[9px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="snapped-expiry">
          {banner || `Listed snapshot → ${snappedExpiry}`}
        </div>
      ) : null}
    </div>
  );
}

// ═══ Mode B Panel — Income Target + Checkboxes + Multi-Strategy ═══
function ModeBPanel({ entries, holdingsCc = [], navigate, dte, onSelectFriday, onUsePolicy, snappedExpiry, expiryPolicy, expiryMode, explicitExpiry, intent, onIntentChange, regimeFitOnly, onRegimeFitOnlyChange, scope, onScopeChange, universeSize, netliq, exclusionCounts, exclusionGroups, statusLine, nextCursor, remainingAfterBatch, onShowMore, showMoreLoading, hideIlliquid, onHideIlliquidChange, hiddenIlliquidCount, cacheAsOf, cacheStatus, preparation, onPrepare, prepareBusy, loadState, compactSnapshotVersion, candidateSnapshotVersion, requestId, prepareAck, onRefreshSnapshot, rowRefreshKey, prepSelected = new Set(), setPrepSelected, lastRefreshReports }) {
  const [monthlyTarget, setMonthlyTarget] = useState(1200);
  // Iter143: client-side column sort. `dir` = "desc"|"asc"; first click on a
  // numeric-goodness column defaults to desc (higher = better). Click same
  // header twice = asc; click a different header = new column desc.
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  const _handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === "desc" ? "asc" : "desc");
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
  };
  // Iter146: Suggest Basket modal state + handler.
  const [basketOpen, setBasketOpen] = React.useState(false);
  const [basketLoading, setBasketLoading] = React.useState(false);
  const [basketSuggestion, setBasketSuggestion] = React.useState(null);
  // Iter182 — "Avoid DTEs spanning earnings" checkbox (default ON).
  const [avoidEarnings, setAvoidEarnings] = React.useState(true);
  // Iter153 — live Risk_Budget block for the manual-selection footer.
  // Fetched independently of Suggest Basket clicks so the footer works
  // from first load. Refreshes every 60s to catch account changes.
  const [footerRiskBudget, setFooterRiskBudget] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/account/risk-snapshot?sizing_profile=standard");
        if (!cancelled && data?.risk_budget) setFooterRiskBudget(data.risk_budget);
      } catch (e) { /* silent — footer falls back to legacy view */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const handleSuggestBasket = async (opts = {}) => {
    setBasketOpen(true);
    setBasketLoading(true);
    setBasketSuggestion(null);
    try {
      const { data } = await api.post("/income-engine/suggest-basket", {
        target_premium: periodTarget,
        dte, scope: scope || "All",
        strategy_intent: intent || "Auto",
        regime_fit_only: !!regimeFitOnly,
        batch_size: 100,
        avoid_earnings_dte: avoidEarnings,
        ...(opts.safety_headroom_pct ? { safety_headroom_pct: opts.safety_headroom_pct } : {}),
      });
      setBasketSuggestion(data);
    } catch (e) { console.warn("Suggest basket failed:", e); }
    finally { setBasketLoading(false); }
  };

  // Iter153 — "Right-size to available budget" button in the modal.
  // Re-runs the optimizer with safety_headroom_pct=0.9 so the basket
  // leaves 10% cushion vs available_for_new_trades.
  const handleRightSizeBasket = () => handleSuggestBasket({ safety_headroom_pct: 0.9 });

  const handleAcceptBasket = () => {
    if (!basketSuggestion?.basket) return;
    // Q1 default: only pre-check basket rows. Do NOT auto-switch profile.
    // If sizing_coach chip is present, it stays visible in the summary
    // footer (Iter141 chips still render on the selected set) so user
    // can act on it as an explicit second step.
    const keys = new Set(basketSuggestion.basket.map(r =>
      `${r.ticker}|${r.strategy}|${r.short_strike ?? ""}`
    ));
    setSelected(keys);
    setBasketOpen(false);
  };

  const handleAdjustBasket = () => {
    // Same as accept — pre-check basket rows so user can toggle from the
    // main table. Closes the modal, returning focus to the table.
    handleAcceptBasket();
  };

  const _sortArrow = (col) => sortCol === col ? (sortDir === "desc" ? " ↓" : " ↑") : "";
  // Iter144b: selection state is KEY-based (ticker + strategy + strike) not
  // index-based so appending "Show more" batches doesn't shift existing
  // selections when the underlying array grows.
  const _entryKey = (e) => `${e?.ticker || ""}|${e?.strategy || ""}|${e?.short_strike ?? ""}`;
  const [selected, setSelected] = useState(new Set());
  const [stratFilter, setStratFilter] = useState("All");
  const [overrides, setOverrides] = useState({});
  const [tooltipIdx, setTooltipIdx] = useState(null);
  const [legendOpen, setLegendOpen] = useState(false);

  const periodTarget = Math.round(monthlyTarget * (dte / 30));

  // Apply strategy overrides and compute effective entries
  const effectiveEntries = entries.map((e, i) => {
    const ovr = overrides[i];
    if (!ovr || ovr === e.strategy) return { ...e, overridden: false };
    // Recalculate from comparison data
    const alt = e.comparison?.[ovr];
    if (!alt) return { ...e, overridden: false };
    const c = e.contracts || 1;
    return {
      ...e, strategy: ovr, overridden: true,
      premium: (alt.max_gain / 100) * c,
      max_loss: Math.abs(alt.max_loss) * c,
      capital_req: alt.capital * c,
      yield_pct: alt.capital > 0 ? round2((alt.max_gain / alt.capital) * 100) : 0,
      strikes_label: ovr === "DCS" ? `$${Math.round(e.price * 1.02)}/$${Math.round(e.price * 1.08)}`
                   : ovr === "BPS" ? `$${Math.round(e.price * 0.95)}/$${Math.round(e.price * 0.90)}`
                   : `$${Math.round(e.price * 0.95)}`,
      note: alt.note,
    };
  });

  const effectiveCc = (holdingsCc || []).map(e => ({ ...e, section: "holdings_cc" }));

  // Iter142: regime-fit toggle now enforces "aligns only" at DISPLAY layer.
  // Backend widening (Iter138) + Iter141 auto-select restructure surface
  // BCS/CSP on Bearish/Volatile properly, but the user's stated ON-state
  // expectation is "show ONLY aligns rows". Enforce that here so the toggle
  // is deterministic regardless of any future backend selector changes.
  let filteredEntries = stratFilter === "All" ? effectiveEntries : effectiveEntries.filter(e => e.strategy === stratFilter);
  if (regimeFitOnly) {
    filteredEntries = filteredEntries.filter(e => (e.regime_fit || "aligns") === "aligns");
  }
  // Iter143: apply client-side sort. Only sort a shallow copy so we don't
  // mutate the parent-supplied array. Extractors map header → e.<field>.
  if (sortCol) {
    const _extractors = {
      ticker: e => (e.ticker || "").toUpperCase(),
      sector: e => (e.sector || "").toUpperCase(),
      price: e => e.price || 0,
      iv_rank: e => e.iv_rank || 0,
      wheel_score: e => e.wheel_score || 0,
      capital_req: e => e.capital_req || 0,
      premium: e => (e.premium || 0) * (e.contracts || 1) * 100,
      yield_pct: e => e.yield_pct || 0,
      yield_pct_per_week: e => e.yield_pct_per_week || 0,
      delta: e => Math.abs(e.delta || 0),
      pop: e => e.pop || 0,
      pot: e => e.pot || 0,
    };
    const _ex = _extractors[sortCol];
    if (_ex) {
      filteredEntries = [...filteredEntries].sort((a, b) => {
        const va = _ex(a), vb = _ex(b);
        if (va === vb) return 0;
        if (typeof va === "string") return sortDir === "desc" ? vb.localeCompare(va) : va.localeCompare(vb);
        return sortDir === "desc" ? vb - va : va - vb;
      });
    }
  }

  const selectedEntries = [...effectiveEntries, ...effectiveCc].filter(e => selected.has(_entryKey(e)) && isValidProb(e.pop) && e.selectable !== false);
  const totalPremium = selectedEntries.reduce((s, e) => s + (e.premium || 0) * 100, 0);
  const totalMaxRisk = selectedEntries.reduce((s, e) => s + (e.max_loss || 0), 0);
  const premRiskPct = totalMaxRisk > 0 ? ((totalPremium / totalMaxRisk) * 100).toFixed(1) : "0.0";
  const progressPct = periodTarget > 0 ? Math.min((totalPremium / periodTarget) * 100, 100) : 0;

  // Iter141: portfolio-level risk benchmarks. Extend the existing summary
  // strip with three benchmark chips drawing on standard premium-selling
  // guardrails: capital deployed vs NetLiq (50% ceiling — VIX-scaled),
  // per-trade concentration (5% defined / 1% undefined), and annualized
  // RoR compared to the 0.5%/week rule of thumb. Colors: green (safe),
  // amber (near threshold), red (over).
  // Note: backend already returns `capital_req` as (cap_per_contract × contracts),
  // so it's the total per-row deployment — no need to re-multiply by contracts.
  const totalCapitalDeployed = selectedEntries.reduce(
    (s, e) => s + (e.capital_req || 0), 0
  );
  const capitalPctOfNetliq = netliq > 0 ? (totalCapitalDeployed / netliq) * 100 : 0;
  const capitalBenchLevel = capitalPctOfNetliq >= 50 ? "over" : capitalPctOfNetliq >= 30 ? "near" : "safe";
  const capitalBenchColor = capitalBenchLevel === "over" ? "text-red-400" : capitalBenchLevel === "near" ? "text-amber-500" : "text-emerald-500";

  // Iter153 — Risk_Budget footer (Mini-Spec Part 3) for the MANUAL
  // selection table. Same model as SuggestBasketModal (Iter149) but
  // recomputed LIVE as the user toggles individual checkboxes. Denominator
  // is `available_for_new_trades` (Excess Liquidity × utilization − existing
  // stressed positions), not raw NetLiq.
  //
  // Uses the risk_budget block returned by /suggest-basket — the user must
  // have clicked Suggest Basket at least once for this to be populated. If
  // absent, the footer falls back to the legacy NetLiq framing so we don't
  // break the page for first-time users.
  //
  // Stress math MIRRORS services/margin_math.short_put_pm_margin: OTM →
  // max(strike - underlying×0.85, 0) × 100 × qty. ITM → underlying × 0.15
  // × 100 × qty. Vertical spreads → (width - premium) × 100 × qty. All ×
  // 1.5x stress multiplier. CC → share_covered pool, does NOT draw from
  // Risk_Budget. This is a CLIENT-SIDE COPY that must track the backend —
  // there's a pytest guardrail (`test_iter153_client_side_footer_math`) that
  // asserts identical numbers for a given ticker+strategy+underlying tuple.
  const rb = basketSuggestion?.risk_budget || footerRiskBudget || null;
  const useRiskBudget = !!rb && (rb.available_for_new_trades > 0);

  function _stressedPerContract(e) {
    // CC: share-covered pool, not Risk_Budget.
    if (e.strategy === "CC") return 0;
    // Vertical credit spread (BPS/BCS): (width - premium) × 100 × 1.5.
    if (e.strategy === "BPS" || e.strategy === "BCS") {
      const width = Math.abs((e.short_strike || 0) - (e.long_strike || 0));
      const premium = e.premium || 0;
      return Math.max(0, (width - premium) * 100) * 1.5;
    }
    // Debit spreads (DCS): fully paid, zero margin.
    if (e.strategy === "DCS") return 0;
    // Short put / CSP / JL: PM stress. Use underlying if available; else
    // strike × 0.15 fallback (matches short_put_pm_margin(no-price) path).
    if (e.strategy === "CSP" || e.strategy === "JL") {
      const strike = e.short_strike || 0;
      const under = e.current_price || 0;
      let perShare;
      if (under <= 0) {
        perShare = strike * 0.15;
      } else if (under >= strike) {  // OTM
        perShare = Math.max(0, strike - under * 0.85);
      } else {  // ITM
        perShare = under * 0.15;
      }
      return perShare * 100 * 1.5;
    }
    // Unknown strategy — fall back to capital_req × 1.5 as a rough proxy.
    return (e.capital_req || 0) * 1.5;
  }

  const stressedNewMargin = selectedEntries.reduce(
    (s, e) => s + _stressedPerContract(e) * (e.contracts || 1),
    0,
  );
  const ccSelectedCount = selectedEntries.filter(e => e.strategy === "CC").length;
  const budgetUsedPct = useRiskBudget
    ? (stressedNewMargin / rb.available_for_new_trades) * 100
    : 0;
  const budgetLevel = budgetUsedPct >= 100 ? "over" : budgetUsedPct >= 75 ? "near" : "safe";
  const budgetColor = budgetLevel === "over" ? "text-red-400" : budgetLevel === "near" ? "text-amber-500" : "text-emerald-500";

  const perTradePcts = selectedEntries.map(e => ({
    ticker: e.ticker,
    isUndefined: (e.strategy === "CSP" || e.strategy === "CC"),
    pct: netliq > 0 ? ((e.capital_req || 0) / netliq) * 100 : 0,
  }));
  const worstPerTrade = perTradePcts.reduce((max, p) => {
    const limit = p.isUndefined ? 1.0 : 5.0;
    const over = p.pct - limit;
    return (over > (max?.over ?? -Infinity)) ? { ...p, limit, over } : max;
  }, null);
  const perTradeBenchLevel = !worstPerTrade ? "safe" : worstPerTrade.over > 0 ? "over" : worstPerTrade.over > -0.5 ? "near" : "safe";
  const perTradeBenchColor = perTradeBenchLevel === "over" ? "text-red-400" : perTradeBenchLevel === "near" ? "text-amber-500" : "text-emerald-500";

  const weeksHorizon = dte > 0 ? dte / 7 : 1;
  const annualizedRoR = totalMaxRisk > 0 ? ((totalPremium / totalMaxRisk) * (52 / weeksHorizon) * 100) : 0;
  const weeklyRoR = totalMaxRisk > 0 && weeksHorizon > 0 ? ((totalPremium / totalMaxRisk) * 100 / weeksHorizon) : 0;
  const rorBenchLevel = weeklyRoR >= 2.5 ? "hot" : weeklyRoR >= 0.5 ? "safe" : weeklyRoR > 0 ? "near" : "cold";
  const rorBenchColor = rorBenchLevel === "hot" ? "text-emerald-400 font-bold" : rorBenchLevel === "safe" ? "text-emerald-500" : rorBenchLevel === "near" ? "text-amber-500" : "text-zinc-400";

  const annualizedPace = dte > 0 ? Math.round((totalPremium / dte) * 365) : 0;
  const progressColor = progressPct >= 100 ? "#10B981" : progressPct >= 70 ? "#F59E0B" : "#EF4444";

  const toggle = (e) => {
    if (e.selectable === false || !isValidProb(e.pop)) return;
    setSelected(prev => {
    const k = _entryKey(e);
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
    });
  };
  const togglePrep = (e) => {
    if (!setPrepSelected) return;
    if (!isRefreshEligible(e)) return;
    const k = pairKey(e);
    setPrepSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) {
        next.delete(k);
        return next;
      }
      if (next.size >= PREP_PAIR_LIMIT) return prev;
      next.add(k);
      return next;
    });
  };
  const visiblePairKeys = () => {
    const keys = [];
    const seen = new Set();
    for (const e of [...filteredEntries, ...effectiveCc]) {
      if (!isRefreshEligible(e)) continue;
      const k = pairKey(e);
      if (!e?.ticker || !String(e?.expiry || "").slice(0, 10) || seen.has(k)) continue;
      seen.add(k);
      keys.push(k);
    }
    return keys;
  };
  const selectToTarget = () => {
    const sorted = [...effectiveEntries].sort((a, b) => b.wheel_score - a.wheel_score);
    const next = new Set();
    let sum = 0;
    for (const e of sorted) {
      if (e.selectable === false || !isValidProb(e.pop)) continue;
      if (sum >= periodTarget) break;
      next.add(_entryKey(e));
      sum += (e.premium || 0) * 100;
    }
    setSelected(next);
  };

  function round2(n) { return Math.round(n * 100) / 100; }

  const staleRefreshCount = (entries || []).filter((row) => (
    row?.selectable === false && (
      row?.option_freshness?.status === "prior_session_stale"
      || row?.option_freshness?.status === "stale"
      || String(row?.freshness_label || "").toLowerCase().includes("refresh")
    )
  )).length;

  return (
    <div className="space-y-2" data-testid="mode-b-panel">
      <WheelDtePicker onSelectFriday={onSelectFriday} onUsePolicy={onUsePolicy} snappedExpiry={snappedExpiry} expiryPolicy={expiryPolicy} expiryMode={expiryMode} explicitExpiry={explicitExpiry} />
      <div className="text-[10px] space-y-0.5" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="wheel-option-freshness">
        <div>
          {cacheAsOf
            ? `Cached snapshot fetched ${String(cacheAsOf).slice(0, 16).replace("T", " ")} UTC — not live pricing`
            : "Cache-backed snapshots — not live pricing"}
        </div>
        {compactSnapshotVersion ? (
          <div className="font-mono-data" data-testid="wheel-snapshot-version">
            compact {String(compactSnapshotVersion).slice(0, 40)}
            {candidateSnapshotVersion ? ` · candidate ${String(candidateSnapshotVersion).slice(0, 40)}` : ""}
          </div>
        ) : null}
        {requestId ? <div className="font-mono-data">request {requestId}</div> : null}
      </div>
      {loadState === "timeout" ? (
        <div className="text-[10px] px-0 py-1" style={{ color: "var(--hoip-text-secondary)" }} data-testid="wheel-timeout-state">
          Wheel request timed out. Last successful result is kept — retry Reload cached results.
        </div>
      ) : null}
      {loadState === "error" ? (
        <div className="text-[10px] px-0 py-1" style={{ color: "var(--hoip-text-secondary)" }} data-testid="wheel-error-state">
          Wheel request failed. Last successful result is kept.
        </div>
      ) : null}
      {loadState === "loading" && !entries.length ? (
        <div className="text-[10px] px-0 py-1" data-testid="wheel-loading-state">Loading cached Wheel candidates…</div>
      ) : null}
      {prepareAck ? (
        <div className="text-[10px] px-0 py-1" style={{ color: "var(--hoip-text-secondary)" }} data-testid="wheel-prepare-ack">
          Prepare {prepareAck.status || "—"}
          {prepareAck.batch_id ? ` · ${prepareAck.batch_id}` : ""}
          {` · pairs ${prepareAck.pair_count ?? (prepareAck.items || []).length} · queued ${prepareAck.queued || 0} · completed ${prepareAck.completed || 0} · failed ${prepareAck.failed || 0}`}
          {prepareAck.no_work_reason ? ` · ${prepareAck.no_work_reason}` : ""}
        </div>
      ) : null}
      {preparation?.status_text ? (
        <div className="text-[10px] px-0 py-1" style={{ color: "var(--hoip-text-secondary)" }}
          data-testid="wheel-preparation-status">
          {preparation.status_text}
          {(preparation.completed || preparation.failed) ? (
            <span className="ml-1 font-mono-data" data-testid="wheel-preparation-counts">
              Completed {preparation.completed || 0} · failed {preparation.failed || 0}
              {preparation.queued ? ` · queued ${preparation.queued}` : ""}
            </span>
          ) : null}
        </div>
      ) : null}
      {staleRefreshCount > 0 ? (
        <div className="text-[10px] px-0 py-1" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="wheel-stale-refresh-hint">
          {staleRefreshCount} row{staleRefreshCount === 1 ? "" : "s"} need a snapshot refresh.
          Press Prepare selected DTE data (up to 8 names) or Refresh on a row.
          Screen Basket does not refresh option quotes.
        </div>
      ) : null}
      <WheelRiskHealthStrip />
      <SizingCoachRibbon
        selectedRows={selectedEntries}
        netliq={netliq}
        riskBudget={basketSuggestion?.risk_budget || null}
        lastBasketCoach={basketSuggestion?.sizing_coach || null} />
      {/* Income Target Bar */}
      <div className="border rounded-sm p-3 space-y-2" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }} data-testid="income-target-bar">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-tertiary)" }}>Income Target</span>
          <div className="flex items-center gap-1">
            <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>$</span>
            <input type="number" value={monthlyTarget} onChange={e => setMonthlyTarget(Number(e.target.value) || 0)}
              className="w-20 h-6 text-xs font-mono-data bg-transparent border rounded-sm px-1.5 text-right"
              style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
              data-testid="income-target-input" />
            <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>/month</span>
            {/* Iter146: Target-driven basket optimizer trigger. */}
            <button onClick={handleSuggestBasket}
              className="h-6 px-2 text-[10px] font-bold rounded-sm bg-blue-600 hover:bg-blue-700 text-white transition-colors ml-1"
              data-testid="suggest-basket-btn"
              title="Auto-propose a basket of trades that hits your target within sizing caps">
              Screen Basket
            </button>
            {/* Iter182 — earnings-DTE filter toggle for the basket screener. */}
            <label className="flex items-center gap-1 text-[10px] ml-1 cursor-pointer select-none"
              style={{ color: "var(--hoip-text-tertiary)" }}
              title="Exclude strike/DTE combinations that would still be open through a ticker's next earnings date"
              data-testid="avoid-earnings-toggle">
              <input type="checkbox" checked={avoidEarnings}
                onChange={e => setAvoidEarnings(e.target.checked)}
                className="accent-emerald-500" />
              Avoid earnings DTEs
            </label>
          </div>
          <button onClick={selectToTarget} className="h-6 px-2 text-[10px] border rounded-sm hover:bg-[var(--hoip-hover)] transition-colors"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }} data-testid="select-to-target-btn">
            Select to target
          </button>
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between text-[10px]">
            <span style={{ color: "var(--hoip-text-tertiary)" }}>Selected trades: <span className="font-mono-data font-bold" style={{ color: progressColor }}>${Math.round(totalPremium)}</span></span>
            <span style={{ color: "var(--hoip-text-tertiary)" }}>Target: <span className="font-mono-data">${periodTarget}</span></span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--hoip-hover)" }}>
            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${progressPct}%`, background: progressColor }} data-testid="income-progress-bar" />
          </div>
          <div className="flex items-center justify-between text-[9px] font-mono-data">
            <span style={{ color: "var(--hoip-text-tertiary)" }}>{totalPremium > 0 && dte > 0 ? `≈ $${annualizedPace.toLocaleString()}/year at this pace` : ""}</span>
            <span style={{ color: progressColor }}>{progressPct.toFixed(0)}% of period target</span>
          </div>
        </div>
      </div>

      {/* Strategy Intent + Show Filter */}
      <div className="flex items-center gap-4 flex-wrap" data-testid="strategy-controls">
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-tertiary)" }}>Strategy Intent:</span>
          <select value={intent} onChange={e => {
              const v = e.target.value;
              setStratFilter(v === "Auto" ? "All" : v);
              setSelected(new Set());
              onIntentChange(v);
            }}
            className="h-6 text-xs bg-transparent border rounded-sm px-1 font-medium"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
            data-testid="strategy-intent-select">
            <option value="Auto">Auto</option>
            <option value="CSP">CSP</option>
            <option value="BPS">BPS</option>
            <option value="BCS">BCS</option>
            <option value="CC">CC</option>
            <option value="DCS">DCS</option>
            <option value="JL">JL (Jade Lizard)</option>
          </select>
        </div>
        {/* Iter138: Regime-fit toggle — when OFF, backend keeps Bearish-Trend
            candidates and rows carry their `regime_fit` tag so users can
            audit out-of-regime ideas (e.g. BCS during Bullish trends). */}
        <label className="flex items-center gap-1 cursor-pointer select-none"
          data-testid="regime-fit-only-toggle-wrap">
          <input
            type="checkbox"
            checked={!!regimeFitOnly}
            onChange={e => onRegimeFitOnlyChange && onRegimeFitOnlyChange(e.target.checked)}
            className="accent-blue-600 w-3 h-3"
            data-testid="regime-fit-only-toggle"
          />
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-tertiary)" }}>
            Filter to regime-fit only
          </span>
        </label>
        {/* Iter156: Liquidity filter — default ON. Hides rows whose chosen-
            strategy short leg has thin OI (<100) or wide spread (>10% mid).
            Hidden count is surfaced next to the label so it's transparent. */}
        <label className="flex items-center gap-1 cursor-pointer select-none"
          data-testid="hide-illiquid-toggle-wrap">
          <input
            type="checkbox"
            checked={!!hideIlliquid}
            onChange={e => onHideIlliquidChange && onHideIlliquidChange(e.target.checked)}
            className="accent-blue-600 w-3 h-3"
            data-testid="hide-illiquid-toggle"
          />
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-tertiary)" }}>
            Hide thin/illiquid options
            {typeof hiddenIlliquidCount === "number" && hiddenIlliquidCount > 0 && hideIlliquid && (
              <span className="ml-1 font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="hidden-illiquid-count">
                ({hiddenIlliquidCount} hidden)
              </span>
            )}
          </span>
        </label>
        {/* Iter139: Scope selector — pre-filter Mode B universe to All /
            Watchlist / a single sector before ranking. Ranking is unchanged. */}
        <div className="flex items-center gap-1" data-testid="scope-selector-wrap">
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-tertiary)" }}>Scope:</span>
          <select value={scope || "All"} onChange={e => onScopeChange && onScopeChange(e.target.value)}
            className="h-6 text-xs bg-transparent border rounded-sm px-1 font-medium"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
            data-testid="scope-selector">
            <option value="All">All</option>
            <option value="Watchlist">My Watchlist</option>
            <option value="Technology">Technology</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Financial Services">Financial Services</option>
            <option value="Energy">Energy</option>
            <option value="Industrials">Industrials</option>
            <option value="Consumer Cyclical">Consumer Cyclical</option>
            <option value="Communication Services">Communication Services</option>
            <option value="Consumer Defensive">Consumer Defensive</option>
            <option value="Utilities">Utilities</option>
            <option value="Real Estate">Real Estate</option>
            <option value="Basic Materials">Basic Materials</option>
            <option value="Defense & Aerospace">Defense &amp; Aerospace</option>
            <option value="Index Funds">Index Funds</option>
          </select>
          {typeof universeSize === "number" && (
            <span className="text-[9px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}
              data-testid="scope-universe-size">({universeSize} tks)</span>
          )}
        </div>
        <div className="flex items-center gap-2" data-testid="strategy-filter-bar">
          <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Show:</span>
          {STRAT_ALL.map(s => (
          <button key={s} onClick={() => {
            // Iter221 — chips now drive a SERVER-side strategy_intent refetch
            // (previously client-side only: filtering 24 Auto-picked rows left
            // 0–3 visible per strategy and "Show more" barely added any).
            setStratFilter(s);
            setSelected(new Set());
            const mapped = s === "All" ? "Auto" : s;
            if (onIntentChange && mapped !== intent) onIntentChange(mapped);
          }}
            className={`h-6 px-2.5 text-[10px] font-medium rounded-full border transition-colors ${
              stratFilter === s ? "bg-blue-600 text-white border-blue-600" : "border-[var(--hoip-border)] text-[var(--hoip-text-secondary)] hover:bg-[var(--hoip-hover)]"
            }`} data-testid={`filter-${s.toLowerCase()}`}>{s}</button>
        ))}
        </div>
        <button onClick={() => setLegendOpen(!legendOpen)} className="text-[9px] ml-auto underline cursor-pointer"
          style={{ color: "var(--hoip-text-tertiary)" }} data-testid="strategy-legend-toggle">
          {legendOpen ? "Hide" : "How strategies are selected"}
        </button>
      </div>

      {/* Collapsible Strategy Legend */}
      {legendOpen && (
        <div className="border rounded-sm p-2 text-[9px] space-y-0.5" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)", color: "var(--hoip-text-tertiary)" }}
          data-testid="strategy-legend">
          <div><span className="font-mono-data text-yellow-500">IVR &lt; 35</span> + Bullish → <span className="font-bold text-yellow-500">DCS</span> (cheap options, buy the move)</div>
          <div><span className="font-mono-data">IVR 35–60</span> + Bullish → <span className="font-bold text-emerald-400">CSP</span> (moderate IV, sell premium, enter at discount)</div>
          <div><span className="font-mono-data text-red-400">IVR &gt; 60</span> + Bullish → <span className="font-bold text-blue-400">BPS</span> (high IV, defined risk, collect fat premium)</div>
          <div><span className="font-mono-data text-red-400">IVR &gt; 60</span> + Volatile → <span className="font-bold text-blue-400">BPS</span> (high IV + uncertainty = define your risk)</div>
          <div>Any + Bearish → <span className="font-bold text-red-400">Parameters Outside Standard Band</span> (no wheel entries in bearish regime)</div>
        </div>
      )}

      <div className="text-[10px] px-0 pb-1" style={{ color: "var(--hoip-text-secondary)" }} data-testid="wheel-select-legend">
        <span className="font-medium" style={{ color: "var(--hoip-text-secondary)" }}>Trade</span> adds a validated row to the basket.
        <span className="font-medium ml-2" style={{ color: "var(--hoip-text-secondary)" }}>Refresh</span> selects the ticker + expiry for data prep (max {PREP_PAIR_LIMIT}), even when Trade is blocked.
        Rows stay readable either way.
      </div>
      {/* Multi-Strategy Table */}
      <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
        <table className="w-full text-left" data-testid="mode-b-table">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
              <th className="text-center px-1 py-2 align-bottom" style={{ minWidth: 52 }}>
                <div className="flex flex-col items-center gap-0.5">
                  <span className="text-[9px] font-medium leading-none" style={{ color: "var(--hoip-text-tertiary)" }}>Trade</span>
                  <input type="checkbox" className="h-3.5 w-3.5 accent-blue-600"
                    data-testid="mode-b-select-all-trade"
                    title="Select all tradeable visible rows"
                    checked={(() => {
                      const tradeable = filteredEntries.filter((e) => e.selectable !== false && isValidProb(e.pop));
                      return tradeable.length > 0 && tradeable.every((e) => selected.has(_entryKey(e)));
                    })()}
                    onChange={() => {
                      const tradeable = filteredEntries.filter((e) => e.selectable !== false && isValidProb(e.pop));
                      const allOn = tradeable.length > 0 && tradeable.every((e) => selected.has(_entryKey(e)));
                      setSelected(allOn ? new Set() : new Set(tradeable.map(_entryKey)));
                    }} />
                </div>
              </th>
              <th className="text-center px-1 py-2 align-bottom" style={{ minWidth: 58 }}>
                <div className="flex flex-col items-center gap-0.5">
                  <span className="text-[9px] font-medium leading-none" style={{ color: "var(--hoip-text-tertiary)" }}>Refresh</span>
                  <input type="checkbox" className="h-3.5 w-3.5 accent-zinc-400"
                    data-testid="mode-b-select-all-prep"
                    title={`Select first ${PREP_PAIR_LIMIT} refresh-eligible ticker+expiry pairs`}
                    checked={(() => {
                      const keys = visiblePairKeys().slice(0, PREP_PAIR_LIMIT);
                      return keys.length > 0 && keys.every((k) => prepSelected && prepSelected.has(k));
                    })()}
                    onChange={() => {
                      if (!setPrepSelected) return;
                      const keys = visiblePairKeys().slice(0, PREP_PAIR_LIMIT);
                      const allOn = keys.length > 0 && keys.every((k) => prepSelected && prepSelected.has(k));
                      setPrepSelected(allOn ? new Set() : new Set(keys));
                    }} />
                </div>
              </th>
              <th className="text-left text-[10px] font-medium px-1 cursor-pointer select-none hover:text-white" style={{ color: "var(--hoip-text-tertiary)" }} onClick={() => _handleSort("ticker")} data-testid="sort-ticker">Ticker{_sortArrow("ticker")}</th>
              <th className="text-left text-[10px] font-medium cursor-pointer select-none hover:text-white" style={{ color: "var(--hoip-text-tertiary)" }} onClick={() => _handleSort("sector")} data-testid="sort-sector">Sector{_sortArrow("sector")}</th>
              <th className="text-right text-[10px] font-medium cursor-pointer select-none hover:text-white" style={{ color: "var(--hoip-text-tertiary)" }} onClick={() => _handleSort("price")} data-testid="sort-price">Price{_sortArrow("price")}</th>
              <th className="text-right text-[10px] font-medium cursor-pointer select-none hover:text-white" style={{ color: "var(--hoip-text-tertiary)" }} onClick={() => _handleSort("iv_rank")} data-testid="sort-ivr"
                title="Percentile rank of current valid ATM IV in a 252-day valid-IV window. Shown only when 60+ observations exist. Never a default 30.">IV Rank{_sortArrow("iv_rank")}</th>
              <th className="text-right text-[10px] font-medium cursor-pointer select-none hover:text-white" style={{ color: "var(--hoip-text-tertiary)" }} onClick={() => _handleSort("wheel_score")} data-testid="sort-score">Score{_sortArrow("wheel_score")}</th>
              <th className="text-center text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Strategy</th>
              <th className="text-left text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Strikes</th>
              <th className="text-center text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Cts</th>
              <th className="text-right text-[10px] font-medium cursor-pointer select-none hover:text-white" style={{ color: "var(--hoip-text-tertiary)" }} onClick={() => _handleSort("capital_req")} data-testid="sort-capital">Capital{_sortArrow("capital_req")}</th>
              <th className="text-right text-[10px] font-medium cursor-pointer select-none hover:text-white" style={{ color: "var(--hoip-text-tertiary)" }} onClick={() => _handleSort("premium")} data-testid="sort-premium">Premium{_sortArrow("premium")}</th>
              <th className="text-right text-[10px] font-medium cursor-pointer select-none hover:text-white" style={{ color: "var(--hoip-text-tertiary)" }} onClick={() => _handleSort("yield_pct")} data-testid="sort-ror" title="RoR — Return on Risk over the trade period: premium / capital-at-risk × 100.">RoR{_sortArrow("yield_pct")}</th>
              <th className="text-right text-[10px] font-medium cursor-pointer select-none hover:text-white" style={{ color: "var(--hoip-text-tertiary)" }} onClick={() => _handleSort("yield_pct_per_week")} data-testid="sort-ypw" title="Weekly return-on-risk (industry-standard premium-selling convention)">Y%/wk{_sortArrow("yield_pct_per_week")}</th>
              <th className="text-right text-[10px] font-medium cursor-pointer select-none hover:text-white" style={{ color: "var(--hoip-text-tertiary)" }} onClick={() => _handleSort("delta")} data-testid="sort-delta" title="Δ — real chain values.">Δ{_sortArrow("delta")}</th>
              <th className="text-right text-[10px] font-medium cursor-pointer select-none hover:text-white" style={{ color: "var(--hoip-text-tertiary)" }} onClick={() => _handleSort("pop")} data-testid="sort-pop" title="Probability of Profit (1 − |Δ|)">POP{_sortArrow("pop")}</th>
              <th className="text-right text-[10px] font-medium cursor-pointer select-none hover:text-white" style={{ color: "var(--hoip-text-tertiary)" }} onClick={() => _handleSort("pot")} data-testid="sort-pot" title="Probability of Touch = 1 − P(non-touch)">POT{_sortArrow("pot")}</th>
              <th className="text-center text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Fit</th>
              <th className="text-left text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Regime</th>
            </tr>
          </thead>
          <tbody>
            {filteredEntries.map((e, fi) => {
              const origIdx = effectiveEntries.indexOf(e);
              return (
              <ModeBRow
                key={`${e.ticker}-${e.strategy}-${e.expiry || ''}-${fi}`}
                e={e} origIdx={origIdx} fi={fi} navigate={navigate}
                selected={selected} toggle={toggle} entryKey={_entryKey}
                prepSelected={prepSelected} togglePrep={togglePrep}
                overrides={overrides} setOverrides={setOverrides}
                tooltipIdx={tooltipIdx} setTooltipIdx={setTooltipIdx}
                onRefreshSnapshot={onRefreshSnapshot} refreshBusy={rowRefreshKey}
                preparation={preparation} prepareAck={prepareAck}
                lastRefreshReports={lastRefreshReports}
              />
            );})}
            {filteredEntries.length === 0 && (
              <tr><td colSpan={19} className="text-center py-6 text-xs" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="empty-state-message">
                {loadState === "timeout" ? (
                  <div className="font-medium">Request timed out — not a confirmed empty Wheel result.</div>
                ) : loadState === "error" ? (
                  <div className="font-medium">Request failed — not a confirmed empty Wheel result.</div>
                ) : loadState === "loading" ? (
                  <div className="font-medium">Loading cached Wheel candidates…</div>
                ) : cacheStatus === "preparing" ? (
                  <div className="flex flex-col gap-2">
                    <div className="font-medium">Preparing selected expiry snapshot…</div>
                    {preparation?.status_text ? (
                      <div className="text-[10px] max-w-xl mx-auto" data-testid="empty-state-preparation">{preparation.status_text}</div>
                    ) : null}
                  </div>
                ) : entries.length === 0 ? (
                  <div className="flex flex-col gap-2">
                    <div className="font-medium">
                      {statusLine || `0 candidates passed current filters for ${intent === "BPS" ? "BPS" : (intent && intent !== "Auto" ? intent : (scope || "All"))}`}
                      {typeof universeSize === "number" && ` (universe: ${universeSize} tickers)`}
                    </div>
                    {preparation?.status_text ? (
                      <div className="text-[10px] max-w-xl mx-auto" data-testid="empty-state-preparation">
                        {preparation.status_text}
                      </div>
                    ) : null}
                    {intent === "CC" && (
                      <div className="text-[10px]" data-testid="cc-empty-hint">
                        No eligible covered-call positions. Covered Calls require at least 100 unreserved shares of the underlying.
                      </div>
                    )}
                    {exclusionCounts && Object.keys(exclusionCounts).length > 0 && (
                      <div className="flex flex-col gap-0.5 text-[10px]" data-testid="empty-state-exclusions">
                        <div className="font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>
                          {Object.keys(exclusionCounts).length === 1 ? "Top exclusion reason:" : "Top exclusion reasons:"}
                        </div>
                        {Object.entries(exclusionCounts)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 4)
                          .map(([reason, count]) => (
                            <div key={reason} className="font-mono-data">
                              <span className="font-bold" style={{ color: "var(--hoip-text-secondary)" }}>{count}</span>
                              {" "}
                              <span>{({
                                "not_in_scanner_cache": "not in Scanner cache (run Scanner to add)",
                                "already_in_open_positions": "already in your open positions",
                                "quality_gate_fund_lt_4": "fundamental score < 4 (quality gate)",
                                "quality_gate_tech_lt_4": "technical score < 4 (quality gate)",
                                "quality_gate_fund_lt_6": "fundamental score < 6 (quality gate)",
                                "quality_gate_tech_lt_6": "technical score < 6 (quality gate)",
                                "regime_bearish_and_filter_on": "Bearish Trend regime + regime-fit filter ON",
                                "no_price_data": "no price data",
                                "yield_below_floor": `below yield floor (< ${0.5}%/wk — try lowering)`,
                                "no_viable_chain_or_provider_error": "no viable option chain (provider error or expiry match)",
                                "below_top_ranked_cap": "outside top-24 ranked cap (use Show More when available)",
                                "cache_preparing": "option snapshots still filling — queued in next chain batch",
                                "held": "already in your holdings",
                                "insufficient_liquidity": "insufficient option liquidity",
                                "nearest_expiry_insufficient_liquidity": "insufficient option liquidity",
                                "regime_mismatch": "regime not ready or filtered",
                                "regime_filter_excluded": "regime filter excluded",
                                "earnings_window": "earnings inside the selected DTE window",
                                "earnings_filter_excluded": "earnings filter excluded",
                                "unavailable_chain": "option snapshot unavailable",
                                "yield_floor": "below yield floor",
                                "premium_unavailable": "premium not available in snapshot",
                                "nearest_expiry_premium_unavailable": "nearest expiry premium unavailable",
                                "outside_selected_dte_tolerance": "expiry outside selected DTE tolerance",
                                "no_listed_expiry_inside_tolerance": "no listed expiry inside selected policy",
                                "nearest_expiry_snapshot_preparing": "nearest expiry snapshot preparing",
                                "bcs_call_leg_snapshot_preparing": "BCS call-leg snapshot preparing",
                                "long_call_unavailable": "long call unavailable",
                                "short_call_unavailable": "short call unavailable",
                                "invalid_reversed_zero_width_structure": "invalid/reversed/zero-width structure",
                                "strategy_structure_unavailable": "OTM premium/leg snapshot preparing",
                                "otm_snapshot_preparing": "OTM premium/leg snapshot preparing",
                                "no_validated_otm_contract": "No validated OTM contract available",
                                "option_timestamp_unverified": "option timestamp unverifiable — refresh required",
                                "prior_session_option_stale": "prior-session option snapshot — refresh required",
                                "stale_option_refresh_pending": "stale option snapshot — refresh required",
                                "spot_moved_option_stale": "underlying moved since option observation",
                                "delta_outside_profile_tolerance": "delta outside profile tolerance",
                                "mismatched_snapshot_version": "mismatched option snapshot version",
                                "option_snapshot_unavailable": "option snapshot unavailable",
                                "risk_or_capital_excluded": "risk or capital excluded",
                                "dte_mismatch": "expiry outside selected DTE tolerance",
                              }[reason] || reason)}</span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ) : stratFilter === "CC"
                  ? "No eligible covered-call positions. Covered Calls require at least 100 unreserved shares of the underlying."
                  : `No ${stratFilter} strategies in current results.`}
              </td></tr>
            )}
          </tbody>
          {effectiveCc.length > 0 && (
            <tbody data-testid="holdings-cc-section">
              <tr>
                <td colSpan={19} className="px-3 py-2 text-[11px] font-bold" data-testid="holdings-cc-heading"
                  style={{ color: "var(--hoip-text-secondary)", background: "var(--hoip-bg)" }}>
                  Covered Calls on Current Holdings
                </td>
              </tr>
              {effectiveCc.map((e, fi) => (
                <ModeBRow
                  key={`cc-${e.ticker}-${e.strategy}-${e.expiry || ""}-${fi}`}
                  e={e} origIdx={10000 + fi} fi={fi} navigate={navigate}
                  selected={selected} toggle={toggle} entryKey={_entryKey}
                  prepSelected={prepSelected} togglePrep={togglePrep}
                  overrides={overrides} setOverrides={setOverrides}
                  tooltipIdx={tooltipIdx} setTooltipIdx={setTooltipIdx}
                  onRefreshSnapshot={onRefreshSnapshot} refreshBusy={rowRefreshKey}
                  preparation={preparation} prepareAck={prepareAck}
                  lastRefreshReports={lastRefreshReports}
                />
              ))}
            </tbody>
          )}
        </table>
        {/* Sticky Sum Footer */}
        {(entries.length > 0 || effectiveCc.length > 0) && (
          <div className="px-3 py-2 border-t space-y-1.5"
            style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }} data-testid="mode-b-footer">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-4 text-[10px]">
                <span className="font-bold" style={{ color: "var(--hoip-text-secondary)" }}>{selected.size} trades selected</span>
                <span style={{ color: "var(--hoip-text-tertiary)" }} data-testid="prep-pair-count">
                  <span className="font-mono-data font-bold">{prepSelected ? prepSelected.size : 0}</span> / {PREP_PAIR_LIMIT} selected for refresh
                </span>
                <span style={{ color: "var(--hoip-text-tertiary)" }}>Total Premium: <span className="font-mono-data text-emerald-500 font-bold">${Math.round(totalPremium)}</span></span>
                <span style={{ color: "var(--hoip-text-tertiary)" }}>Max Risk: <span className="font-mono-data text-red-400">${totalMaxRisk.toLocaleString()}</span></span>
                <span style={{ color: "var(--hoip-text-tertiary)" }}>Premium/Risk: <span className="font-mono-data font-medium">{premRiskPct}%</span></span>
              </div>
              <div className="flex items-center gap-2">
                {/* Iter144b: Show more — cursor-based append. Preserves
                    checkbox selections (key-based) and appends the next
                    batch of ranked candidates below existing rows. */}
                {typeof nextCursor === "number" && remainingAfterBatch > 0 && (
                  <button
                    onClick={onShowMore}
                    disabled={showMoreLoading}
                    className="h-7 px-3 text-[10px] font-medium rounded-sm border transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--hoip-hover)]"
                    style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
                    data-testid="mode-b-show-more">
                    {showMoreLoading ? "Loading…" : `Show more (${remainingAfterBatch} remaining)`}
                  </button>
                )}
                <button disabled={selected.size === 0}
                  className="h-7 px-4 text-[10px] font-bold rounded-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="execute-all-btn">Send Selected to Broker</button>
              </div>
            </div>
            {prepSelected && prepSelected.size > 0 ? (
              <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="prep-not-basket-note">
                Selected for data refresh, not added to trade basket.
              </div>
            ) : null}
            {/* Iter141: portfolio-level risk benchmarks — capital-vs-NetLiq,
                per-trade concentration, annualized RoR vs the 0.5%/week
                rule of thumb. Colored chips flag over/near/safe zones. */}
            {selected.size > 0 && netliq > 0 && (
              <div className="flex items-center gap-3 text-[9px] pt-1 border-t flex-wrap"
                style={{ borderColor: "var(--hoip-divider)" }} data-testid="risk-benchmarks">
                {useRiskBudget ? (
                  <span style={{ color: "var(--hoip-text-tertiary)" }} data-testid="footer-risk-budget">
                    New risk:{" "}
                    <span className={`font-mono-data font-bold ${budgetColor}`}
                      data-testid="footer-stressed-margin"
                      title={`Stressed by 1.5× multiplier. Denominator: available_for_new_trades = Risk_Budget − existing stressed usage.`}>
                      ${Math.round(stressedNewMargin).toLocaleString()} stressed ({budgetUsedPct.toFixed(1)}% of budget)
                    </span>
                    <span className="ml-1" style={{ color: "var(--hoip-text-tertiary)" }}>
                      · EL ${Math.round(rb.excess_liquidity).toLocaleString()}
                      {" "}→ Budget ({Math.round(rb.utilization_factor * 100)}% util) ${Math.round(rb.risk_budget).toLocaleString()}
                      {" "}→ After existing (${Math.round(rb.existing_options_stressed_usage).toLocaleString()} stressed) ${Math.round(rb.available_for_new_trades).toLocaleString()} available
                    </span>
                    {ccSelectedCount > 0 && (
                      <span className="ml-1" style={{ color: "var(--hoip-text-tertiary)" }}>
                        · {ccSelectedCount} CC row(s) share-covered (not in budget)
                      </span>
                    )}
                  </span>
                ) : (
                  <span style={{ color: "var(--hoip-text-tertiary)" }}>
                    Capital deployed:{" "}
                    <span className={`font-mono-data font-bold ${capitalBenchColor}`}
                      data-testid="bench-capital"
                      title={`${capitalBenchLevel === "over" ? "OVER" : capitalBenchLevel === "near" ? "NEAR" : "SAFE"} the ~50% NetLiq ceiling (tighter in high-VIX regimes)`}>
                      {capitalPctOfNetliq.toFixed(1)}% of NetLiq
                    </span>
                    <span className="ml-1" style={{ color: "var(--hoip-text-tertiary)" }}>
                      (safe &lt; 30%, near 30-50%, over ≥ 50% · click Suggest Basket once to unlock Risk_Budget view)
                    </span>
                  </span>
                )}
                <span style={{ color: "var(--hoip-text-tertiary)" }}>
                  Per-trade max:{" "}
                  <span className={`font-mono-data font-bold ${perTradeBenchColor}`}
                    data-testid="bench-per-trade"
                    title={worstPerTrade ? `Largest position: ${worstPerTrade.ticker} at ${worstPerTrade.pct.toFixed(2)}% of NetLiq (limit ${worstPerTrade.limit}% for ${worstPerTrade.isUndefined ? "undefined-risk" : "defined-risk"})` : "no positions"}>
                    {worstPerTrade ? `${worstPerTrade.pct.toFixed(2)}% (${worstPerTrade.ticker})` : "—"}
                  </span>
                  <span className="ml-1" style={{ color: "var(--hoip-text-tertiary)" }}>(≤1% undefined, ≤5% defined)</span>
                </span>
                <span style={{ color: "var(--hoip-text-tertiary)" }}>
                  RoR:{" "}
                  <span className={`font-mono-data ${rorBenchColor}`}
                    data-testid="bench-ror"
                    title={`Weekly: ${weeklyRoR.toFixed(2)}%/wk · Annualized: ${annualizedRoR.toFixed(0)}%/yr vs the 0.5%/wk (~26%/yr) baseline`}>
                    {weeklyRoR.toFixed(2)}%/wk (≈ {annualizedRoR.toFixed(0)}%/yr)
                  </span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Iter146: Suggest Basket modal — target-driven optimizer. */}
      <SuggestBasketModal
        open={basketOpen}
        suggestion={basketSuggestion}
        loading={basketLoading}
        onAccept={handleAcceptBasket}
        onAdjust={handleAdjustBasket}
        onRightSize={handleRightSizeBasket}
        onCancel={() => setBasketOpen(false)} />
    </div>
  );
}

// ═══ PIE Section (Income Engine) ═══
export function PIESection() {
  const navigate = useNavigate();
  const incomeReq = useRef(0);
  const incomeAbort = useRef(null);
  // Iter F cleanup (P0 hotfix): the /pie/settings endpoint was removed
  // when the Traditional PIE drawer was deleted. The loadSettings call
  // now 404s and left `settings === null` forever, freezing the whole
  // page on "Loading engine settings…". Seed with a stub so the page
  // renders immediately; the drawer that consumed these values is gone.
  const [settings, setSettings] = useState({
    target_premium_usd: 1000,
    dte_preference: 35,
    risk_profile: "proposed",
    vol_toggle: "low",
  });
  const [cl, setCl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rationaleOpen, setRationaleOpen] = useState(false);
  const [executing, setExecuting] = useState({});
  const [replacing, setReplacing] = useState({});
  const [engineMode, setEngineMode] = useState("traditional");
  const [tradeScreens, setTradeScreens] = useState([]);
  const [screenLoading, setScreenLoading] = useState(false);
  // Iter B (iter133): Traditional PIE moved into a collapsed drawer per
  // spec §2 — kept for one release, then removed in follow-up.
  const [tradPieOpen, setTradPieOpen] = useState(false);
  // Iter A (Wheel-Strategies spec §1): both panels always visible on
  // desktop, stacked below 1024 px. `incomeMode` is retained only as
  // a mobile-visibility hint (unused on desktop) but the two data
  // buckets always load in a single round-trip via `mode=all`.
  const [incomeExisting, setIncomeExisting] = useState(null);
  const [incomeNewEntries, setIncomeNewEntries] = useState(null);
  const [prepareBusy, setPrepareBusy] = useState(false);
  const [prepareAck, setPrepareAck] = useState(null);
  const [prepSelected, setPrepSelected] = useState(new Set());
  const [rowRefreshKey, setRowRefreshKey] = useState(null);
  const [lastRefreshReports, setLastRefreshReports] = useState({});
  const [incomeLoading, setIncomeLoading] = useState(false);
  const [incomeLoadState, setIncomeLoadState] = useState("loading");
  const [modeBDte, setModeBDte] = useState(38);
  const [expiryMode, setExpiryMode] = useState("policy");
  const [explicitExpiry, setExplicitExpiry] = useState(null);
  const [modeBIntent, setModeBIntent] = useState("Auto");
  // Iter138: regime-fit toggle. Default ON per spec — filters out
  // Bearish-Trend candidates. When OFF, all regimes flow and rows keep
  // their regime_fit tag so out-of-regime ideas remain auditable.
  const [regimeFitOnly, setRegimeFitOnly] = useState(true);
  // Iter139: Mode B universe scope (All / Watchlist / <sector>). Default
  // "All" preserves current behavior; ranking downstream is unchanged.
  const [modeBScope, setModeBScope] = useState("All");
  // Iter144b: Show More loading flag (visual feedback while cursor batch fetches).
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  // Iter D (iter135): shared yield floor filter. Feeds both Mode A (tag
  // sub-floor legs) and Mode B (split candidates into headline + below-floor).
  const [minYield, setMinYield] = useState(0.5);  // Iter D: show/hide the below-floor candidates drawer in Mode B.
  const [showBelowFloor, setShowBelowFloor] = useState(false);
  // Iter157: show/hide the illiquid-filter drawer.
  const [showHiddenIlliquid, setShowHiddenIlliquid] = useState(false);
  // Iter156: liquidity filter toggle — default ON per user spec. Hides
  // rows whose chosen-strategy short leg has thin OI (<100) or wide
  // spread (>10% of mid). Hidden rows count is exposed so the UI can
  // show a transparent nudge instead of silently dropping.
  const [hideIlliquid, setHideIlliquid] = useState(true);
  // Iter222 — pricing basis: "mid" vs "natural" (conservative bid/ask estimate).
  const [pricingBasis, setPricingBasis] = useState("mid");
  // Iter D: which advisor "why" popover is expanded? key=`${ticker}-${li}`
  const [expandedWhy, setExpandedWhy] = useState(null);
  // Iter E (iter136): Sosnoff sizing profile — Conservative / Standard / Aggressive.
  // Feeds Mode B's `contracts` clamp on the backend.
  const [sizingProfile, setSizingProfile] = useState("standard");
  const [sizingMeta, setSizingMeta] = useState(null); // {pct_per_trade, netliq, daily_theta_target}

  // Legacy shims — Iter B removes them once ranker wiring is done.
  const [incomeMode, setIncomeMode] = useState("existing");
  const incomeData = incomeMode === "existing" ? incomeExisting : incomeNewEntries;

  // Iter F: /pie/settings endpoint removed with the Traditional PIE drawer.
  // loadSettings + useEffect deleted; `settings` is now a static stub above.
  // Iter A: single mount-time round-trip loads both Mode A + Mode B.
  useEffect(() => { loadIncomeEngine("new_entries"); }, []); // eslint-disable-line
  // Iter E (iter136): fetch active sizing profile on mount so the toggle
  // reflects the persisted server state, not the local default.
  useEffect(() => { loadSizingProfile(); }, []); // eslint-disable-line
  useEffect(() => { setPrepSelected(new Set()); }, [modeBDte, expiryMode, explicitExpiry, modeBIntent, modeBScope]);

  const saveSettings = async (u) => {
    const m = { ...settings, ...u }; setSettings(m);
    try { await api.put("/pie/settings", m); } catch (e) { console.warn(e); }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pie/curated-list");
      setCl(data);
      // Auto-screen if in reliability mode
      if (engineMode === "regime" && data?.trades?.length > 0) {
        screenTrades(data.trades);
      }
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
  };

  const screenTrades = async (trades) => {
    setScreenLoading(true);
    try {
      const items = trades.map(t => ({
        ticker: t.ticker, strategy: t.strategy, dte: t.dte,
      }));
      const { data } = await api.post("/regime/evaluate/batch", { items });
      setTradeScreens(data.results || []);
    } catch (e) { console.warn(e); setTradeScreens([]); }
    finally { setScreenLoading(false); }
  };

  const loadIncomeEngine = async (mode, dteVal, intentVal, yieldVal, regimeFitOnlyVal, scopeVal, hideIlliquidVal, pricingVal, opts) => {
    const silent = Boolean(opts && opts.silent);
    const nextMode = (opts && opts.expiryMode) || expiryMode || "policy";
    const nextExp = opts && Object.prototype.hasOwnProperty.call(opts, "selectedExpiry")
      ? opts.selectedExpiry
      : explicitExpiry;
    if (!silent) {
      setIncomeLoading(true);
      setIncomeLoadState((prev) => (incomeNewEntries ? prev : "loading"));
    }
    const reqId = ++incomeReq.current;
    if (incomeAbort.current) incomeAbort.current.abort();
    const ac = new AbortController();
    incomeAbort.current = ac;
    try {
      const d = dteVal ?? modeBDte;
      const si = intentVal ?? modeBIntent;
      const my = yieldVal ?? minYield;
      const rfo = regimeFitOnlyVal ?? regimeFitOnly;
      const sc = scopeVal ?? modeBScope;
      const hi = hideIlliquidVal ?? hideIlliquid;
      const pb = pricingVal ?? pricingBasis;
      const effectiveMode = mode || "all";
      const qs = new URLSearchParams({
        mode: effectiveMode,
        dte: String(d),
        capital: "100000",
        strategy_intent: String(si),
        min_yield_pct_per_week: String(my),
        regime_fit_only: rfo ? "true" : "false",
        scope: String(sc),
        cursor: "0",
        batch_size: "20",
        hide_illiquid: hi ? "true" : "false",
        pricing: String(pb),
        expiry_mode: nextMode,
      });
      if (nextMode === "explicit" && nextExp) qs.set("selected_expiry", nextExp);
      const { data } = await api.get(`/income-engine?${qs.toString()}`, { timeout: 8000, signal: ac.signal });
      if (reqId !== incomeReq.current) return;
      const panel = effectiveMode === "existing" ? data : (data.new_entries || data);
      const ef = panel?.effective_filters || {};
      if (ef.target_dte != null && Number(ef.target_dte) !== Number(d)) return;
      const fam = String(ef.strategy_family || ef.strategy_intent || "").toUpperCase();
      const want = String(si || "").toUpperCase();
      if (fam && want && fam !== want) return;
      if (effectiveMode === "all") {
        setIncomeExisting(data.existing || null);
        setIncomeNewEntries(data.new_entries || null);
      } else if (effectiveMode === "existing") {
        setIncomeExisting(data);
      } else {
        setIncomeNewEntries(data);
      }
      const st = (panel && panel.cache_status) || "ready";
      const n = ((panel && panel.entries) || []).length;
      if (st === "preparing" && n === 0) setIncomeLoadState("preparing");
      else if (n === 0) setIncomeLoadState("empty");
      else setIncomeLoadState("ready");
    } catch (e) {
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      const timedOut = e?.code === "ECONNABORTED" || /timeout/i.test(String(e?.message || ""));
      setIncomeLoadState(timedOut ? "timeout" : "error");
      console.warn(e);
    }
    finally { if (!silent) setIncomeLoading(false); }
  };
  const loadIncomeEngineRef = useRef(loadIncomeEngine);
  loadIncomeEngineRef.current = loadIncomeEngine;
  const handlePrepareDte = async () => {
    setPrepareBusy(true);
    try {
      const pairs = [...prepSelected].slice(0, PREP_PAIR_LIMIT).map((k) => {
        const [ticker, expiry] = String(k).split("|");
        return { ticker, expiry };
      }).filter((p) => p.ticker && p.expiry && p.expiry.length >= 10);
      const { data } = await api.post("/income-engine/prepare-dte", {
        dte: modeBDte,
        strategy_intent: modeBIntent,
        scope: modeBScope,
        expiry_mode: expiryMode,
        selected_expiry: expiryMode === "explicit" ? explicitExpiry : undefined,
        ...(pairs.length ? { pairs } : {}),
      }, { timeout: 5000 });
      setPrepareAck(data || null);
      await loadIncomeEngine("new_entries");
    } catch (e) { console.warn(e); }
    finally { setPrepareBusy(false); }
  };
  const handleRefreshRow = async (row) => {
    if (!row?.ticker || !row?.expiry || rowRefreshKey) return;
    const key = `${row.ticker}|${row.expiry}`;
    setRowRefreshKey(key);
    try {
      const q = new URLSearchParams({ expiry: String(row.expiry).slice(0, 10), force: "true" });
      if (row.strategy) q.set("strategy", row.strategy);
      const { data } = await api.post(`/ticker/${row.ticker}/option-snapshot/refresh?${q}`, null, { timeout: 25000 });
      const report = data?.refresh_report || {
        ticker: row.ticker,
        expiry: String(row.expiry).slice(0, 10),
        outcome: data?.refresh_outcome,
        providers_tried: Object.keys(data?.provider_timings || {}).map((k) => String(k).replace(/_ms$/, "")),
        option_quote_as_of: data?.option_freshness?.option_quote_as_of || data?.option_quote_as_of,
        option_snapshot_fetched_at: data?.option_freshness?.option_snapshot_fetched_at,
        retry_after_sec: data?.retry_after_sec,
        quote_timestamp_quality: data?.quote_timestamp_quality || data?.option_freshness?.quote_timestamp_quality,
        freshness_label: data?.freshness_label || data?.option_freshness?.user_label,
      };
      setLastRefreshReports((prev) => ({ ...prev, [key]: report }));
      await loadIncomeEngine("new_entries", undefined, undefined, undefined, undefined, undefined, undefined, undefined, { silent: true });
    } catch (e) { console.warn(e); }
    finally { setRowRefreshKey(null); }
  };
  useEffect(() => {
    const preparingBcs = modeBIntent === "BCS" && incomeNewEntries?.cache_status === "preparing";
    if (!incomeNewEntries?.preparation?.active && !preparingBcs) return undefined;
    const id = setInterval(() => {
      loadIncomeEngineRef.current("new_entries", undefined, undefined, undefined, undefined, undefined, undefined, undefined, { silent: true });
    }, 8000);
    return () => clearInterval(id);
  }, [incomeNewEntries?.preparation?.active, incomeNewEntries?.preparation?.completed, incomeNewEntries?.cache_status, modeBIntent]);
  // Iter144b: DTE change now auto-refetches thanks to the 90s chain+IVR
  // cache. Previously the flow was "change DTE → click Refresh" because
  // refetches were 8s; now the cache warm-path makes DTE feel like a
  // live filter. Also DTE change resets cursor (the sort order changes).
  const handleSelectFriday = (iso, newDte) => {
    setExpiryMode("explicit");
    setExplicitExpiry(iso);
    setModeBDte(newDte);
    loadIncomeEngine("new_entries", newDte, modeBIntent, minYield, undefined, undefined, undefined, undefined, {
      expiryMode: "explicit", selectedExpiry: iso,
    });
  };
  const handleUsePolicy = () => {
    setExpiryMode("policy");
    setExplicitExpiry(null);
    setModeBDte(38);
    loadIncomeEngine("new_entries", 38, modeBIntent, minYield, undefined, undefined, undefined, undefined, {
      expiryMode: "policy", selectedExpiry: null,
    });
  };
  const handleModeBDteChange = (newDte) => {
    setModeBDte(newDte);
    loadIncomeEngine("new_entries", newDte, modeBIntent, minYield);
  };
  // Iter144b: Show More — fetch NEXT batch and append (do NOT replace).
  const handleShowMore = async () => {
    const cur = incomeNewEntries?.next_cursor;
    if (typeof cur !== "number" || showMoreLoading) return;
    setShowMoreLoading(true);
    try {
      const qs = new URLSearchParams({
        mode: "new_entries",
        dte: String(modeBDte),
        capital: "100000",
        strategy_intent: String(modeBIntent),
        min_yield_pct_per_week: String(minYield),
        regime_fit_only: regimeFitOnly ? "true" : "false",
        scope: String(modeBScope),
        cursor: String(cur),
        batch_size: "20",
        hide_illiquid: hideIlliquid ? "true" : "false",
        pricing: String(pricingBasis),
        expiry_mode: expiryMode,
      });
      if (expiryMode === "explicit" && explicitExpiry) qs.set("selected_expiry", explicitExpiry);
      const { data } = await api.get(`/income-engine?${qs.toString()}`, { timeout: 8000 });
      // Append the fresh window to existing entries. Preserve everything
      // else that's already loaded (below_floor list stays, summary stats
      // rebuild from the union).
      setIncomeNewEntries(prev => {
        if (!prev) return data;
        const merged = [...(prev.entries || []), ...(data.entries || [])];
        return {
          ...prev,
          entries: merged,
          count: merged.length,
          next_cursor: data.next_cursor,
          remaining_after_batch: data.remaining_after_batch,
        };
      });
    } catch (e) { console.warn("Show More failed:", e); }
    finally { setShowMoreLoading(false); }
  };
  const handleModeBIntentChange = (newIntent) => {
    setModeBIntent(newIntent);
    loadIncomeEngine("new_entries", modeBDte, newIntent, minYield);
  };
  const handleMinYieldChange = (v) => {
    // Clamp to backend-approved range [0.25, 5.0].
    const clamped = Math.max(0.25, Math.min(5.0, Number(v) || 0.5));
    setMinYield(clamped);
    loadIncomeEngine("new_entries", modeBDte, modeBIntent, clamped);
  };
  // Iter138: regime-fit toggle handler — re-fetch Mode B on flip.
  const handleRegimeFitOnlyChange = (v) => {
    setRegimeFitOnly(v);
    loadIncomeEngine("new_entries", modeBDte, modeBIntent, minYield, v);
  };
  // Iter139: scope selector handler — re-fetch Mode B when scope changes.
  const handleScopeChange = (s) => {
    setModeBScope(s);
    // Iter143: clear stale entries immediately so the empty-state doesn't
    // linger from the previous (potentially empty) scope while the new
    // batch loads. Testing agent flagged a scope-cycle lag on the empty
    // state after switching Basic Materials → All.
    setIncomeNewEntries(prev => prev ? { ...prev, entries: [], count: 0, universe_size: null } : prev);
    loadIncomeEngine("new_entries", modeBDte, modeBIntent, minYield, regimeFitOnly, s);
  };
  // Iter156: liquidity filter toggle handler.
  const handleHideIlliquidChange = (v) => {
    setHideIlliquid(v);
    loadIncomeEngine("new_entries", modeBDte, modeBIntent, minYield, regimeFitOnly, modeBScope, v);
  };

  // Iter222 — pricing basis handler: re-fetch Mode B on flip.
  const handlePricingBasisChange = (v) => {
    setPricingBasis(v);
    loadIncomeEngine("new_entries", modeBDte, modeBIntent, minYield, regimeFitOnly, modeBScope, hideIlliquid, v);
  };

  // Iter E (iter136): sizing profile handlers
  const loadSizingProfile = async () => {
    try {
      const { data } = await api.get("/pie-settings/sizing-profile");
      setSizingProfile(data.sizing_profile || "standard");
      setSizingMeta(data);
    } catch (e) { console.warn(e); }
  };
  const handleSizingProfileChange = async (nextProfile) => {
    setSizingProfile(nextProfile);
    try {
      await api.post("/pie-settings/sizing-profile", { sizing_profile: nextProfile });
      // Refresh meta + re-fetch Mode B with the new clamps
      await loadSizingProfile();
      loadIncomeEngine("new_entries");
    } catch (e) { console.warn(e); }
  };

  // Screen trades when switching to reliability mode with existing list
  useEffect(() => {
    if (engineMode === "regime" && cl?.trades?.length > 0 && tradeScreens.length === 0) {
      screenTrades(cl.trades);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineMode]);

  const executeTrade = async (t, idx) => {
    setExecuting(p => ({ ...p, [idx]: true }));
    try { await api.post("/pie/execute-trade", t); } catch (e) { console.warn(e); }
    finally { setExecuting(p => ({ ...p, [idx]: "done" })); }
  };

  const replaceTicker = async (t, idx) => {
    setReplacing(p => ({ ...p, [idx]: true }));
    try {
      const exclude = cl.trades.map(tr => tr.ticker);
      const { data } = await api.post("/pie/replace-ticker", { ticker: t.ticker, sector: t.sector, exclude });
      if (data.replacement) {
        setCl(prev => {
          const trades = [...prev.trades];
          const alt = data.replacement;
          trades[idx] = { ...trades[idx], ticker: alt.ticker, current_price: alt.current_price || trades[idx].current_price,
            tech_score: alt.technical_score || 0, fund_score: alt.fundamental_score || 0, sector: alt.sector || trades[idx].sector };
          return { ...prev, trades };
        });
      }
    } catch (e) { console.warn(e); }
    finally { setReplacing(p => ({ ...p, [idx]: false })); }
  };

  const earnBadge = (f) => {
    if (f === "clean") return <span className="text-emerald-500 text-[9px]"><CheckCircle className="w-2.5 h-2.5 inline" /></span>;
    if (f === "pre_earn") return <span className="text-emerald-500 text-[9px]"><CheckCircle className="w-2.5 h-2.5 inline" /> pre</span>;
    return <span className="text-yellow-500 text-[9px]"><AlertTriangle className="w-2.5 h-2.5 inline" /></span>;
  };

  if (!settings) return (
    <div className="space-y-3" data-testid="pie-section">
      {/* Iter F fix (A): removed duplicate 'Wheel Income Engine' header —
          the page-level H1 in WheelIncomePage.js is the only header. */}
      <div className="space-y-3" data-testid="income-panels-loading">
        <div className="border rounded-sm p-4" style={{ borderColor: "var(--hoip-border)" }}>
          <div className="text-[11px] font-bold mb-2" data-testid="income-mode-b">New-Entry Candidates</div>
          <div className="text-center py-4 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>Loading engine settings…</div>
        </div>
      </div>
    </div>
  );
  const ps = cl?.portfolio_summary;
  const ts = cl?.theta_summary;
  const su = cl?.settings_used;
  const ba = cl?.bucket_allocation || [];
  const pieData = ba.filter(b => b.premium > 0).map(b => ({ name: b.label, value: b.premium }));

  return (
    <div className="space-y-3" data-testid="pie-section">
      {/* Iter A rework (iter132): STACKED, full-width layout — Existing
          on top, New Entries below. Side-by-side was rejected. Legacy
          `income-mode-a` / `income-mode-b` testids are re-purposed as
          PANEL HEADERS so downstream tests keep working. */}
      <div className="flex items-center justify-between">
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          {/* Iter E (iter136): Sosnoff sizing toggle — feeds Mode B's clamp */}
          <div className="flex items-center gap-1"
            data-testid="sizing-toggle"
            title={sizingMeta ? `${(sizingMeta.pct_per_trade * 100).toFixed(2)}% / trade · $${(sizingMeta.netliq / 1000).toFixed(0)}k NetLiq · θ target $${sizingMeta.daily_theta_target}/day · short |Δ| ${sizingMeta.delta_targets?.short ?? ""}` : ""}>
            {[
              { p: "conservative", tip: "Short put |Δ| 0.18 · long wing 0.07 · smallest sizing" },
              { p: "standard", tip: "Short put |Δ| 0.28 · long wing 0.10 · standard sizing" },
              { p: "aggressive", tip: "Short put |Δ| 0.35 · long wing 0.14 · largest sizing" },
            ].map(({ p, tip }) => (
              <button
                key={p}
                type="button"
                title={tip}
                onClick={() => handleSizingProfileChange(p)}
                className={`text-[10px] px-2 py-1 border rounded-sm font-medium transition-colors ${
                  sizingProfile === p
                    ? "bg-blue-500/15 text-blue-400 border-blue-500/30"
                    : "hover:bg-[var(--hoip-hover)]"
                }`}
                style={sizingProfile === p ? {} : { borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
                data-testid={`sizing-profile-${p}`}>
                {p[0].toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
          {/* Iter D (iter135): shared yield-floor control feeds both panels */}
          <label className="flex items-center gap-1 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            <span title="Mid: observed bid/ask mid, else bid, else ask, else last. Natural: sell at bid and buy at ask when those quotes exist — never a fabricated 0.85/1.10 haircut.">Pricing</span>
            <select value={pricingBasis} onChange={(ev) => handlePricingBasisChange(ev.target.value)}
              className="h-6 text-[10px] bg-transparent border rounded-sm px-1 font-medium"
              style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
              data-testid="pricing-basis-select">
              <option value="mid">Mid</option>
              <option value="natural">Natural</option>
            </select>
          </label>
          <label className="flex items-center gap-1 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            <span title="Minimum return-on-risk per week (premium / strike / DTE × 7)">Yield floor</span>
            <Input type="number" step="0.05" min="0.25" max="5.0"
              value={minYield}
              onChange={(ev) => handleMinYieldChange(ev.target.value)}
              className="w-16 h-6 text-[10px] font-mono-data"
              data-testid="min-yield-input" />
            <span>%/wk</span>
          </label>
          <button data-testid="income-reload-cached"
            onClick={() => loadIncomeEngine("new_entries")}
            disabled={incomeLoading}
            title="Re-read the latest Wheel candidate cache from Mongo. Zero provider calls."
            className="text-[10px] px-2 py-1 border rounded-sm flex items-center gap-1"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>
            {incomeLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Reload cached results
          </button>
          <button data-testid="income-prepare-dte"
            onClick={handlePrepareDte}
            disabled={prepareBusy}
            title={prepSelected.size
              ? `Queue compact refresh for ${prepSelected.size} selected ticker+expiry pair(s) (max ${PREP_PAIR_LIMIT}). Zero vendor on this click.`
              : `Queue up to ${PREP_PAIR_LIMIT} incomplete ticker/expiry pairs for the selected DTE. Use the Refresh boxes to choose which. Zero vendor on this click.`}
            className="text-[10px] px-2 py-1 border rounded-sm flex items-center gap-1"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>
            {prepareBusy ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {prepSelected.size ? `Prepare ${prepSelected.size} selected pair${prepSelected.size === 1 ? "" : "s"}` : "Prepare selected DTE data"}
          </button>
        </div>
      </div>

      {/* Stacked full-width panels — Existing Positions on top, then
          New-Entry Candidates directly below. Both take 100% width. */}
      <div className="space-y-3">
        {/* ── PANEL: New-Entry Candidates (Mode B) ── */}
        <div className="border rounded-sm overflow-hidden"
             style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
             data-testid="income-panel-new-entries">
          <div className="px-3 py-2 border-b flex items-center justify-between"
               style={{ borderColor: "var(--hoip-border)" }}>
            <div className="text-[11px] font-bold" data-testid="income-mode-b">
              New-Entry Candidates
            </div>
            <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
              {(incomeNewEntries?.entries || []).length} row{(incomeNewEntries?.entries || []).length === 1 ? "" : "s"}
            </div>
          </div>
          <ModeBPanel entries={incomeNewEntries?.entries || []}
            holdingsCc={incomeNewEntries?.holdings_cc || []}
            statusLine={incomeNewEntries?.status_line}
            exclusionGroups={incomeNewEntries?.exclusion_groups}
            navigate={navigate}
            dte={modeBDte} onSelectFriday={handleSelectFriday} onUsePolicy={handleUsePolicy}
            snappedExpiry={incomeNewEntries?.snapped_expiry}
            expiryPolicy={incomeNewEntries?.effective_filters?.expiry_policy}
            expiryMode={incomeNewEntries?.expiry_mode || expiryMode}
            explicitExpiry={explicitExpiry}
            intent={modeBIntent} onIntentChange={handleModeBIntentChange}
            regimeFitOnly={regimeFitOnly} onRegimeFitOnlyChange={handleRegimeFitOnlyChange}
            scope={modeBScope} onScopeChange={handleScopeChange}
            universeSize={incomeNewEntries?.universe_size}
            netliq={incomeNewEntries?.sizing_netliq || 0}
            exclusionCounts={incomeNewEntries?.exclusion_counts || {}}
            nextCursor={incomeNewEntries?.next_cursor}
            remainingAfterBatch={incomeNewEntries?.remaining_after_batch}
            onShowMore={handleShowMore}
            showMoreLoading={showMoreLoading}
            hideIlliquid={hideIlliquid}
            onHideIlliquidChange={handleHideIlliquidChange}
            hiddenIlliquidCount={incomeNewEntries?.hidden_illiquid_count || 0}
            cacheAsOf={incomeNewEntries?.cache_as_of}
            cacheStatus={incomeNewEntries?.cache_status}
            preparation={incomeNewEntries?.preparation}
            onPrepare={handlePrepareDte}
            prepareBusy={prepareBusy}
            loadState={incomeLoadState}
            compactSnapshotVersion={incomeNewEntries?.compact_snapshot_version || incomeNewEntries?.effective_filters?.compact_snapshot_version}
            candidateSnapshotVersion={incomeNewEntries?.candidate_snapshot_version}
            requestId={incomeNewEntries?.request_id}
            prepareAck={prepareAck}
            onRefreshSnapshot={handleRefreshRow}
            rowRefreshKey={rowRefreshKey}
            lastRefreshReports={lastRefreshReports}
            prepSelected={prepSelected}
            setPrepSelected={setPrepSelected} />
          {/* Iter D (iter135): below-floor drawer — like-for-like audit list */}
          {(incomeNewEntries?.below_floor?.length || 0) > 0 && (
            <div className="border-t px-3 py-2" style={{ borderColor: "var(--hoip-border)" }}>
              <button
                type="button"
                onClick={() => setShowBelowFloor(!showBelowFloor)}
                data-testid="below-floor-toggle"
                className="text-[10px] flex items-center gap-1 opacity-70 hover:opacity-100"
                style={{ color: "var(--hoip-text-secondary)" }}>
                {showBelowFloor ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {incomeNewEntries.below_floor.length} hidden below yield floor ({minYield}%/wk) — click to reveal
              </button>
              {showBelowFloor && (
                <div className="mt-2 opacity-60" data-testid="below-floor-drawer">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                        <th className="text-left text-[9px] font-medium px-2 py-1" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
                        <th className="text-center text-[9px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Strategy</th>
                        <th className="text-left text-[9px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Strikes</th>
                        <th className="text-right text-[9px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Y%/wk</th>
                        <th className="text-right text-[9px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Δ</th>
                        <th className="text-right text-[9px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>POP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {incomeNewEntries.below_floor.map((be, bi) => (
                        <tr key={`bf-${be.ticker}-${bi}`}
                          className="border-b text-[9px]"
                          style={{ borderColor: "var(--hoip-divider)" }}
                          data-testid={`below-floor-row-${be.ticker}`}>
                          <td className="font-mono-data px-2 py-1 text-blue-400">
                            {be.ticker}
                            <span className="ml-1 text-[8px] px-1 rounded-sm bg-zinc-500/15 text-zinc-400">below floor</span>
                          </td>
                          <td className="text-center">
                            <span className={`text-[8px] px-1 rounded-sm font-bold ${STRAT_COLORS[be.strategy] || "bg-zinc-500/10 text-zinc-400"}`}>
                              {be.strategy}
                            </span>
                          </td>
                          <td className="font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>{be.strikes_label}</td>
                          <td className="text-right font-mono-data">{be.yield_pct_per_week}%</td>
                          <td className="text-right font-mono-data">{be.delta != null ? Math.abs(be.delta).toFixed(2) : "—"}</td>
                          <td className="text-right font-mono-data">{formatProbPct(be.pop)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {/* Iter157: hidden-illiquid drawer — surfaces the specific rows the
              liquidity filter dropped so users can calibrate the OI/spread
              thresholds against their real universe. */}
          {(incomeNewEntries?.hidden_illiquid?.length || 0) > 0 && (
            <div className="border-t px-3 py-2" style={{ borderColor: "var(--hoip-border)" }}>
              <button
                type="button"
                onClick={() => setShowHiddenIlliquid(!showHiddenIlliquid)}
                data-testid="hidden-illiquid-toggle"
                className="text-[10px] flex items-center gap-1 opacity-70 hover:opacity-100"
                style={{ color: "var(--hoip-text-secondary)" }}>
                {showHiddenIlliquid ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {incomeNewEntries.hidden_illiquid.length} hidden — thin liquidity (OI &lt; 100 or spread &gt; 10% mid). Click to reveal.
              </button>
              {showHiddenIlliquid && (
                <div className="mt-2 opacity-60" data-testid="hidden-illiquid-drawer">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                        <th className="text-left text-[9px] font-medium px-2 py-1" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
                        <th className="text-center text-[9px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Strategy</th>
                        <th className="text-left text-[9px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Strikes</th>
                        <th className="text-right text-[9px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Bid</th>
                        <th className="text-right text-[9px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Ask</th>
                        <th className="text-right text-[9px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Spread %</th>
                        <th className="text-right text-[9px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>OI</th>
                        <th className="text-left text-[9px] font-medium px-2" style={{ color: "var(--hoip-text-tertiary)" }}>Why filtered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {incomeNewEntries.hidden_illiquid.map((he, hi) => {
                        const liq = he.liquidity || {};
                        return (
                          <tr key={`hi-${he.ticker}-${hi}`}
                            className="border-b text-[9px]"
                            style={{ borderColor: "var(--hoip-divider)" }}
                            data-testid={`hidden-illiquid-row-${he.ticker}`}>
                            <td className="font-mono-data px-2 py-1 text-blue-400">
                              {he.ticker}
                              <span className="ml-1 text-[8px]" style={{ color: "var(--hoip-text-tertiary)" }}>thin</span>
                            </td>
                            <td className="text-center">
                              <span className={`text-[8px] px-1 rounded-sm font-bold ${STRAT_COLORS[he.strategy] || "bg-zinc-500/10 text-zinc-400"}`}>
                                {he.strategy}
                              </span>
                            </td>
                            <td className="font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>{he.strikes_label}</td>
                            <td className="text-right font-mono-data">{liq.bid != null ? `$${liq.bid}` : "—"}</td>
                            <td className="text-right font-mono-data">{liq.ask != null ? `$${liq.ask}` : "—"}</td>
                            <td className="text-right font-mono-data">{liq.spread_pct != null ? `${liq.spread_pct.toFixed(1)}%` : "—"}</td>
                            <td className="text-right font-mono-data">{liq.open_interest != null ? liq.open_interest : "—"}</td>
                            <td className="px-2" style={{ color: "var(--hoip-text-secondary)" }}>{liq.thin_reason || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Silence unused-var lint for the legacy `incomeData` / `incomeMode`
          shims kept for backward compat with tests that may still poke
          them via useState devtools. */}
      {false && <span style={{ display: "none" }}>{incomeMode}{JSON.stringify(incomeData)?.length}</span>}

    </div>
  );
}
